import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { spacingMobile } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * MobilePreviewModal Component
 *
 * Fullscreen preview modal for mobile devices.
 * Shows ASCII art at optimal size without layout constraints.
 */
const MobilePreviewModal = ({ isOpen, onClose, asciiPreview }) => {
  const { colors } = useTheme();
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: colors.background,
      zIndex: 2000,
      display: 'flex',
      flexDirection: 'column',
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Header with Close Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: spacingMobile.md,
        borderBottom: `1px solid ${colors.border}`,
        background: colors.backgroundPanel,
        flexShrink: 0
      }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: 600,
          color: colors.foreground
        }}>
          Preview
        </span>
        <button
          onClick={onClose}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.foreground,
            transition: 'all 0.2s'
          }}
          aria-label="Close preview"
        >
          <X size={24} />
        </button>
      </div>

      {/* Scrollable Preview Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacingMobile.lg
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {asciiPreview}
        </div>
      </div>

      {/* Hint Text */}
      <div style={{
        padding: spacingMobile.md,
        textAlign: 'center',
        fontSize: '0.8125rem',
        color: colors.foregroundSecondary,
        background: colors.backgroundPanel,
        borderTop: `1px solid ${colors.border}`
      }}>
        Pinch to zoom • Scroll to view full preview
      </div>
    </div>
  );
};

export default MobilePreviewModal;
